import { Undo2 } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useOrderReturns } from '@/hooks/use-sales-return-query'
import { formatDateTimeVN, formatQty, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    orderId: string
    enabled: boolean
}

/** Lịch sử phiếu trả hàng của đơn đã giao (M-44). Ẩn khi chưa có phiếu. */
export function SalesReturnHistory({ orderId, enabled }: Props) {
    const { data: returns = [], isLoading } = useOrderReturns(orderId, enabled)
    if (!enabled || isLoading || returns.length === 0) return null

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <Undo2 className="size-4" aria-hidden />
                    {m.sales_return_history_title()}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {returns.map((r) => (
                    <div
                        key={r.id}
                        className="space-y-1 border-b pb-3 text-sm last:border-b-0 last:pb-0"
                    >
                        <div className="flex flex-wrap justify-between gap-2">
                            <span className="font-mono font-medium">
                                {r.code}
                            </span>
                            <span className="font-semibold">
                                {formatVnd(r.total_amount)}
                            </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {formatDateTimeVN(r.returned_at)}
                            {r.created_by_name ? ` · ${r.created_by_name}` : ''}
                            {r.stock_in_note_code
                                ? ` · ${r.stock_in_note_code}`
                                : ''}
                        </div>
                        <ul className="text-xs">
                            {r.items.map((it) => {
                                const defect = it.lots.reduce(
                                    (s, l) => s + l.defect_quantity,
                                    0,
                                )
                                return (
                                    <li key={it.id}>
                                        {m.sales_return_history_item({
                                            name: it.product_name ?? '',
                                            qty: formatQty(it.quantity),
                                        })}
                                        {defect > 0
                                            ? ` (${m.sales_return_history_defect({ qty: formatQty(defect) })})`
                                            : ''}
                                    </li>
                                )
                            })}
                        </ul>
                        {r.fee_amount > 0 && (
                            <div className="text-xs">
                                {m.sales_return_history_fee({
                                    amount: formatVnd(r.fee_amount),
                                    reason: r.fee_reason ?? '',
                                })}
                            </div>
                        )}
                        <div className="text-xs">
                            {r.refunded_amount > 0
                                ? m.sales_return_history_refunded({
                                      amount: formatVnd(r.refunded_amount),
                                  })
                                : m.sales_return_history_not_refunded()}
                        </div>
                        {r.note && (
                            <p className="text-xs text-muted-foreground">
                                {r.note}
                            </p>
                        )}
                    </div>
                ))}
            </CardContent>
        </Card>
    )
}
